import PropTypes from "prop-types";
import React, { useState, useRef, useEffect } from "react";
import Heading from "../../elements/heading";
import Input from "../../elements/input";
import PrimaryButton from "../../elements/primary-button";
import SimpleLink from "../../elements/simple-link";
import "./style.scss";
import BodyText from "../../elements/body-text";
import api from "../../utils/api";

const VerifyEmailForm = ({
  formTitleType,
  formTitleBody,
  body,
  verificationCodeLabel,
  verificationCodeInfoText,
  verificationCodeWarningMessage,
  verificationCodeMissingMessage,
  verifyEmailFailedMessage,
  resendCodeButtonText,
  resendSuccessMessage,
  resendReachMaxErrorMessage,
  resendErrorMessage,
  ctaName,
  colorScheme,
  iconName,
  disclaimerText,
  submittedEmail,
  sendCodeFailed,
  verificationFailed,
  codeType,
  onVerificationCodeSubmitted,
  isReachedMax,
  createAccountFailed,
  isNetworkFailed,
  registrationInProcess,
  createAccountFailedMessage,
  loginNetworkErrorMessage,
  registrationInProcessMessage
}) => {
  const submitVerificationCodeFunc = (evt, data) => {
      onVerificationCodeSubmitted(evt, data);
    },
    resendVerificationCodeFunc = async () => {
      setSubmitting(true);
      try {
        await api.verification.send(null, {
          email: submittedEmail,
          type: codeType
        });
        setResendSucessfull(true);
      } catch (ex) {
        if (ex.code === "USER_CUSTOMER_0013") {
          setReachedMax(true);
        } else {
          setResendSucessfull(false);
        }
      } finally {
        setTimeout(() => {
          setSubmitting(false);
        }, 1000);
      }
    };

  const resendButtonRef = useRef();
  const resendMessageRef = useRef();

  const [formDataVerificationCode, setVerificationCode] = useState(null);
  const [showResendButton, setShowResendButton] = useState(true);

  const [reachedMax, setReachedMax] = useState(isReachedMax);
  const [resendSucessfull, setResendSucessfull] = useState(true);
  const [verificationCodeMissing, setVerificationCodeMissing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (resendButtonRef.current && resendMessageRef.current) {
      if (showResendButton) {
        resendButtonRef.current.style.display = "inline-block";
        resendMessageRef.current.style.display = "none";
      } else {
        resendMessageRef.current.style.display = "inline-block";
        resendButtonRef.current.style.display = "none";
      }
    }
  }, [showResendButton]);
  const onVerificationCodeFocus = evt => {
    setVerificationCodeMissing(false);
  };
  const onVerificationCodeChange = evt => {
    setVerificationCode(evt.target.value);
  };

  const onSubmit = evt => {
    evt.preventDefault();
    let isValid = true;
    if (!formDataVerificationCode) {
      isValid = false;
      setVerificationCodeMissing(true);
    } else {
      setVerificationCodeMissing(false);
    }
    if (isValid) {
      setSubmitting(true);
      try {
        let data = { code: formDataVerificationCode.trim() };
        submitVerificationCodeFunc(evt, data);
      } finally {
        setTimeout(() => {
          setSubmitting(false);
        }, 1000);
      }
    }
  };

  const onResendCodeClick = evt => {
    // hide resend button for 5 sec
    setShowResendButton(false);
    setTimeout(() => {
      setShowResendButton(true);
    }, 5000);
    resendVerificationCodeFunc(evt);
  };

  const getResendMessage = () => {
    if (reachedMax) {
      return <span className="error">{resendReachMaxErrorMessage.value}</span>;
    } else if (!resendSucessfull) {
      return <span className="error"> {resendErrorMessage.value}</span>;
    } else {
      return <span> {resendSuccessMessage.value}</span>;
    }
  };

  return (
    <div className={"verify-email-form"}>
      {formTitleBody && (
        <Heading
          type={formTitleType}
          className="title show-on-scroll"
          html={{
            value: `<span>${formTitleBody.value.trim()} ${submittedEmail}`
          }}
          typographyClass="type-h3"
        />
      )}

      {body && <BodyText html={body.value} />}

      {verificationCodeLabel && (
        <Input
          {...{
            label: verificationCodeLabel.value,
            infoMessage: verificationCodeInfoText.value,
            showError: verificationCodeMissing || sendCodeFailed,
            warningMessage: verificationCodeMissing ? verificationCodeMissingMessage.value : verificationCodeWarningMessage.value,
            onChange: evt => onVerificationCodeChange(evt),
            onFocus: evt => onVerificationCodeFocus(evt)
          }}
        />
      )}

      {verificationFailed && verifyEmailFailedMessage.value && <BodyText html={verifyEmailFailedMessage.value} className="failed-message" />}
      {createAccountFailed && <BodyText html={createAccountFailedMessage?.value} className="failed-message" />}
      {isNetworkFailed && <BodyText html={loginNetworkErrorMessage?.value} className="failed-message" />}
      {registrationInProcess && <BodyText html={registrationInProcessMessage?.value} className="failed-message" />}
      <div className="button-container">
        {ctaName && ctaName.value && (
          <PrimaryButton
            {...{
              colorScheme: colorScheme,
              iconName: iconName,
              link: {
                value: {
                  text: ctaName.value
                }
              },
              isDisabled: submitting,
              callback: evt => onSubmit(evt)
            }}
          />
        )}

        {resendCodeButtonText && resendCodeButtonText.value && (
          <SimpleLink
            {...{
              isDisabled: submitting,
              text: resendCodeButtonText.value,
              callback: evt => onResendCodeClick(evt)
            }}
            ref={resendButtonRef}
          />
        )}
        {
          <div className="resend-code-confirmation" ref={resendMessageRef}>
            {getResendMessage()}
          </div>
        }
      </div>

      {disclaimerText && disclaimerText.value !== "" && (
        <div className="change-email-modal-disclaimer">
          <BodyText html={disclaimerText.value} className="disclaimer" />
        </div>
      )}
    </div>
  );
};

VerifyEmailForm.propTypes = {
  formTitleType: PropTypes.object,
  formTitleBody: PropTypes.object,
  body: PropTypes.shape({ value: PropTypes.string }),
  verificationCodeLabel: PropTypes.shape({ value: PropTypes.string }),
  verificationCodeInfoText: PropTypes.shape({ value: PropTypes.string }),
  verificationCodeWarningMessage: PropTypes.shape({ value: PropTypes.string }),
  verifyEmailFailedMessage: PropTypes.shape({ value: PropTypes.string }),
  resendCodeButtonText: PropTypes.shape({ value: PropTypes.string }),
  resendSuccessMessage: PropTypes.shape({ value: PropTypes.string }),
  resendReachMaxErrorMessage: PropTypes.shape({ value: PropTypes.string }),
  resendErrorMessage: PropTypes.shape({ value: PropTypes.string }),
  ctaName: PropTypes.shape({ value: PropTypes.string }),
  colorScheme: PropTypes.object,
  iconName: PropTypes.object,
  disclaimerText: PropTypes.shape({ value: PropTypes.string }),
  submittedEmail: PropTypes.string
};

export default VerifyEmailForm;
